import React, { useEffect, useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Modal from '../../../shared/components/ui/Modal';
import FormInput from '../../../shared/components/ui/FormInput';
import Button from '../../../shared/components/ui/Button';
import { getServiceProviders, updateManagementSubTask, getAllUsersByProjectId, getMyVendors, uploadFileToS3 } from '../../services/api/implementation/impl';
import toast from 'react-hot-toast';
import { TaskStatus, isWorkStarted, toDateOnly } from './taskWorkflow';
import DueDateReasonModal from './DueDateReasonModal';
import { childDueAfterParent, parentDueDate } from './taskTree';
import AssigneePicker from './AssigneePicker';
import { ASSIGNEE_REQUIRED_MESSAGE, assigneeKindFromApi, assigneePayload, hasAssignee, subtaskStaffUserOptions } from './assigneeKind';
import { allocationExceedsBudget, ALLOCATION_EXCEEDED_MESSAGE } from './budget';
import {
    usesPermissionFlow,
    selectableSubtaskTypeOptions,
    SubTaskPermissionType,
    SUBTASK_TYPE_REQUIRED_MESSAGE
} from './permission';

const EditSubtaskModal = ({ onClose, subtask, parentTask }) => {
    // "permission" here means the permission flow — compliance included.
    const isPermission = usesPermissionFlow(parentTask);
    const queryClient = useQueryClient();
    const [showReasonModal, setShowReasonModal] = useState(false);
    const [pendingPayload, setPendingPayload] = useState(null);

    const { data: spData } = useQuery({
        queryKey: ['serviceProviders'],
        queryFn: getServiceProviders
    });

    const spAssigneeOptions = spData?.data?.map(sp => ({
        label: `${sp.user?.name || 'SP'} (${sp.category?.name || ''})`,
        value: sp.id
    })) || [];

    const { data: vendorData } = useQuery({
        queryKey: ['myVendors'],
        queryFn: () => getMyVendors(1, 200)
    });
    const vendorList = vendorData?.data?.data || vendorData?.data || [];
    const vendorOptions = (Array.isArray(vendorList) ? vendorList : [])
        .filter((v) => v.user_id || v.user?.id)
        .map((v) => ({
            label: v.businessName || v.name || v.user?.name || `Vendor #${v.id}`,
            value: v.id
        }));

    const projectId = parentTask?.project_id || subtask?.project_id;
    const { data: usersData } = useQuery({
        queryKey: ['projectUsers', projectId],
        queryFn: () => getAllUsersByProjectId(projectId, 1, 500),
        enabled: !!projectId
    });

    const usersOptions = subtaskStaffUserOptions(usersData?.data, [
        subtask?.assignedTo_id,
        subtask?.approver_id
    ]);

    const { register, control, handleSubmit, formState: { errors }, reset, watch, setValue } = useForm({
        defaultValues: {
            title: subtask?.title || '',
            description: subtask?.description || '',
            assignedTo_id: subtask?.assignedTo_id || '',
            assigneeKind: assigneeKindFromApi(subtask),
            assignedTo_sp_id: subtask?.assignedTo_sp_id || '',
            assignedTo_vendor_id: subtask?.assignedTo_vendor_id || '',
            approver_id: subtask?.approver_id || '',
            permissionType: subtask?.permissionType || '',
            status: subtask?.status || TaskStatus.PLANNED,
            duration: subtask?.duration || 0,
            budget: subtask?.budget ?? '',
            plannedStartDate: subtask?.plannedStartDate ? subtask.plannedStartDate.split('T')[0] : '',
            plannedEndDate: subtask?.plannedEndDate ? subtask.plannedEndDate.split('T')[0] : '',
            docs: subtask?.docs || []
        }
    });

    const { fields: docFields, append: appendDoc, remove: removeDoc } = useFieldArray({
        control,
        name: "docs"
    });

    useEffect(() => {
        if (usersData?.data) {
            reset({
                ...subtask,
                assigneeKind: assigneeKindFromApi(subtask),
                assignedTo_sp_id: subtask?.assignedTo_sp_id || '',
                assignedTo_vendor_id: subtask?.assignedTo_vendor_id || '',
                plannedStartDate: subtask?.plannedStartDate ? subtask.plannedStartDate.split('T')[0] : '',
                plannedEndDate: subtask?.plannedEndDate ? subtask.plannedEndDate.split('T')[0] : '',
                docs: subtask?.docs || []
            });
        }
    }, [usersData, subtask, reset]);

    const updateMutation = useMutation({
        mutationFn: (data) => updateManagementSubTask(subtask.id, data),
        onSuccess: () => {
            toast.success('Subtask updated successfully');
            queryClient.invalidateQueries(['managementTask']);
            onClose();
        },
        onError: (error) => {
            toast.error(error?.response?.data?.message || 'Failed to update subtask');
        }
    });

    const buildPayload = (data) => ({
        title: data.title,
        description: data.description,
        ...assigneePayload(data.assigneeKind, data),
        approver_id: data.approver_id ? Number(data.approver_id) : null,
        permissionType: isPermission ? subtask?.permissionType : undefined,
        // Status is locked in this form (and react-hook-form drops disabled
        // fields), so send the current status back unchanged.
        status: subtask?.status,
        duration: Number(data.duration),
        budget: data.budget === '' || data.budget == null ? null : Number(data.budget),
        plannedStartDate: data.plannedStartDate,
        plannedEndDate: data.plannedEndDate,
        docs: data.docs || [],
        parent_subtask_id: null
    });

    const onSubmit = (data) => {
        if (!hasAssignee(data.assigneeKind, data)) {
            toast.error(ASSIGNEE_REQUIRED_MESSAGE);
            return;
        }
        if (isPermission && !subtask?.permissionType) {
            toast.error(SUBTASK_TYPE_REQUIRED_MESSAGE);
            return;
        }
        const parentBudget = parentTask?.budget;
        const siblings = (parentTask?.subTask || [])
            .map((s) => ({
                parent_subtask_id: null,
                budget: s.id === subtask?.id ? data.budget : s.budget
            }));
        if (allocationExceedsBudget(parentBudget, siblings, null)) {
            toast.error(ALLOCATION_EXCEEDED_MESSAGE);
            return;
        }
        const payload = buildPayload(data);
        const parentDue = parentDueDate(parentTask, subtask, parentTask?.subTask);
        const needsWorkStartedReason =
            isWorkStarted(subtask?.status) &&
            toDateOnly(data.plannedEndDate) !== toDateOnly(subtask?.plannedEndDate);
        const afterParent = childDueAfterParent(data.plannedEndDate, parentDue);
        const dateChanged = toDateOnly(data.plannedEndDate) !== toDateOnly(subtask?.plannedEndDate);
        const needsOverride =
            afterParent &&
            (dateChanged || !String(subtask?.dueDateOverrideReason || '').trim());

        if (afterParent && !needsOverride) {
            payload.dueDateOverrideReason = subtask.dueDateOverrideReason;
        }

        if (needsWorkStartedReason || needsOverride) {
            setPendingPayload({ payload, needsWorkStartedReason, needsOverride });
            setShowReasonModal(true);
            return;
        }
        updateMutation.mutate(payload);
    };

    const assignedToId = watch('assignedTo_id');
    const approverOptions = [
        { label: 'No Approver', value: '' },
        ...usersOptions.filter(user => user.value != assignedToId)
    ];

    const handleFileUpload = async (e, index) => {
        const file = e.target.files[0];
        if (!file) return;

        const toastId = toast.loading('Uploading file...');
        try {
            const result = await uploadFileToS3(file);
            const fileData = result?.data?.files?.[0];
            const uploadedUrl = fileData?.presignedUrl || fileData?.Location || result.Location;

            if (uploadedUrl) {
                setValue(`docs.${index}.url`, uploadedUrl);
                toast.success('File uploaded successfully', { id: toastId });
            } else {
                toast.error('Failed to get file URL', { id: toastId });
            }
        } catch (error) {
            console.error("Upload error:", error);
            toast.error('File upload failed', { id: toastId });
        }
    };

    return (
        <>
        <Modal isOpen={true} onClose={onClose} name="Edit Subtask" width="max-w-4xl">
            <form onSubmit={handleSubmit(onSubmit)} className="p-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                    <FormInput label="Title" register={register} register_key="title" type="text" isRequired={true} width={100} />
                    <FormInput label="Description" register={register} register_key="description" type="text" isRequired={true} width={100} />
                    <AssigneePicker
                        register={register}
                        watch={watch}
                        setValue={setValue}
                        usersOptions={usersOptions}
                        spOptions={spAssigneeOptions}
                        vendorOptions={vendorOptions}
                    />
                    <FormInput label="Approver" register={register} register_key="approver_id" type="dropdown" options={approverOptions} isRequired={false} width={100} />
                    {isPermission && (
                        <FormInput
                            label="Type"
                            register={register}
                            register_key="permissionType"
                            type="dropdown"
                            options={selectableSubtaskTypeOptions({
                                queryRaised:
                                    subtask?.permissionType === SubTaskPermissionType.QUERY_RAISED
                            })}
                            isRequired={true}
                            readOnly={true}
                            width={100}
                        />
                    )}
                    {/* Read-only: status moves through the subtask's actions, not this form. */}
                    <FormInput
                        label="Status"
                        register={register}
                        register_key="status"
                        type="dropdown"
                        options={[
                            {
                                label: subtask?.status || TaskStatus.PLANNED,
                                value: subtask?.status || TaskStatus.PLANNED
                            }
                        ]}
                        isRequired={true}
                        readOnly={true}
                        width={100}
                    />
                    <FormInput label="Allocation (₹)" register={register} register_key="budget" type="number" width={100} />
                    <FormInput label="Duration (Days)" register={register} register_key="duration" type="number" isRequired={true} width={100} CustomFeature={{ min: 1 }} />
                    <FormInput label="Start Date" register={register} register_key="plannedStartDate" type="date" isRequired={true} width={100} />
                    <FormInput label="End Date" register={register} register_key="plannedEndDate" type="date" isRequired={true} width={100} />
                </div>

                <div className="border-t pt-2">
                    <div className="flex justify-between items-center mb-2">
                        <h5 className="text-sm font-semibold text-gray-600">Documents</h5>
                        <button
                            type="button"
                            onClick={() => appendDoc({ name: '', url: '' })}
                            className="text-xs bg-gray-200 hover:bg-gray-300 px-2 py-1 rounded"
                        >
                            + Add Doc
                        </button>
                    </div>
                    {docFields.map((doc, index) => {
                        const currentUrl = watch(`docs.${index}.url`);
                        return (
                            <div key={doc.id} className="grid grid-cols-2 gap-2 mb-2 items-end">
                                <FormInput
                                    label="Doc Name"
                                    register={register}
                                    register_key={`docs.${index}.name`}
                                    type="text"
                                    placeholder="e.g. Spec Sheet"
                                    isRequired={true}
                                    width={100}
                                />
                                <div className="flex gap-2 items-end h-[3.5rem] pb-1">
                                    {currentUrl ? (
                                        <div className="flex items-center gap-2 w-full">
                                            <a href={currentUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline text-sm truncate flex-1 block">View Doc</a>
                                            <input type="hidden" {...register(`docs.${index}.url`)} />
                                            <button type="button" onClick={() => removeDoc(index)} className="text-red-500 hover:text-red-700 ml-2">✕</button>
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-2 w-full">
                                            <div className="flex-1">
                                                <input type="file" onChange={(e) => handleFileUpload(e, index)} className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
                                            </div>
                                            <button type="button" onClick={() => removeDoc(index)} className="text-red-500 hover:text-red-700 mb-2">✕</button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                    <Button type="button" onClick={onClose} variant="secondary">Cancel</Button>
                    <Button type="submit">Update</Button>
                </div>
            </form>
        </Modal>
        {showReasonModal && (
            <DueDateReasonModal
                title={pendingPayload?.needsOverride ? 'Reason for due date' : undefined}
                body={
                    pendingPayload?.needsOverride
                        ? 'This due date is after the parent due date. Enter a reason to override.'
                        : undefined
                }
                onClose={() => {
                    setShowReasonModal(false);
                    setPendingPayload(null);
                }}
                onConfirm={(reason) => {
                    if (!pendingPayload?.payload) return;
                    const next = { ...pendingPayload.payload };
                    if (pendingPayload.needsWorkStartedReason) {
                        next.dueDateChangeReason = reason;
                    }
                    if (pendingPayload.needsOverride) {
                        next.dueDateOverrideReason = reason;
                    }
                    setShowReasonModal(false);
                    setPendingPayload(null);
                    updateMutation.mutate(next);
                }}
            />
        )}
        </>
    );
};

export default EditSubtaskModal;
